/**
 * ReplyInput — text input for replying to pplTalk messages.
 *
 * Anonymous visitors see a "Log in to reply" prompt.
 * Logged-in users get a text input with submit button.
 * Content moderation is handled server-side by replyValidatorEurW.
 */

import React, { useState, useCallback, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { createReply } from '../services/pplTalkService';

interface ReplyInputProps {
  /** The parent message ID to reply to */
  messageId: string;
  /** Callback when a reply is successfully created */
  onReplyCreated: () => void;
}

const ReplyInput: React.FC<ReplyInputProps> = ({ messageId, onReplyCreated }) => {
  const { user } = useAuth();
  const location = useLocation();
  const [text, setText] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const handleSubmit = useCallback(async () => {
    if (!text.trim() || !user || submitting) return;

    setSubmitting(true);
    setError(null);

    try {
      const userAny = user as Record<string, unknown>;
      const userName =
        (userAny.tetherHandle as string) ||
        (userAny.firstName as string) ||
        user.displayName ||
        'Anonymous';

      const result = await createReply({
        text: text.trim(),
        messageId,
        userName,
        userAvatar: (userAny.avatar as string) || null,
      });

      if (result.success) {
        setText('');
        onReplyCreated();
      } else {
        setError(result.reason || 'Your reply was not approved. Please review and try again.');
      }
    } catch (err: unknown) {
      console.error('Error posting reply:', err);
      const firebaseError = err as { code?: string };
      if (firebaseError.code === 'functions/unauthenticated') {
        setError('You must be logged in to reply.');
      } else {
        setError('Something went wrong. Please try again.');
      }
    } finally {
      setSubmitting(false);
    }
  }, [text, user, submitting, messageId, onReplyCreated]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        handleSubmit();
      }
    },
    [handleSubmit]
  );

  // Anonymous state
  if (!user) {
    const redirectUrl = encodeURIComponent(location.pathname + location.search);
    return (
      <div className="reply-input-container reply-input--anonymous">
        <div className="reply-input-prompt">
          <p>log in to join the conversation</p>
          <Link to={`/login?redirect=${redirectUrl}`} className="btn btn-primary btn-sm">
            log in
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="reply-input-container">
      <div className="reply-input-row">
        <textarea
          ref={textareaRef}
          className="reply-input-textarea"
          placeholder="Write a reply..."
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={handleKeyDown}
          disabled={submitting}
          rows={2}
          maxLength={2000}
        />
        <button
          className="btn btn-primary reply-submit-btn"
          onClick={handleSubmit}
          disabled={submitting || !text.trim()}
          type="button"
        >
          {submitting ? '...' : 'reply'}
        </button>
      </div>
      {error && <p className="reply-input-error">{error}</p>}
    </div>
  );
};

export default ReplyInput;
